import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { stripLocalePrefix } from "@/hooks/useLocalePath";

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  locale?: string;
  alternateLocale?: string;
  /** Override canonical path (useful for blog articles with language-specific slugs) */
  canonicalPath?: string;
  /** Custom hreflang entries (overrides default behavior) */
  hreflangAlternates?: { lang: string; href: string }[];
  /** If true, adds noindex,follow robots meta */
  noindex?: boolean;
}

// Single source of truth for the canonical domain
const SITE_URL = "https://www.thewisecompass.com";

const defaultTitle = "Wise Compass | Eco-Friendly Brands";
const defaultDescription = "Discover and compare eco-friendly brands. Make informed choices for a sustainable lifestyle with Wise Compass.";
const defaultImage = `${SITE_URL}/og-image-wise-en.png`;

const SEO = ({
  title,
  description = defaultDescription,
  image = defaultImage,
  locale = "en_US",
  alternateLocale = "fr_FR",
  canonicalPath,
  hreflangAlternates,
  noindex = false,
}: SEOProps) => {
  const { pathname } = useLocation();
  const fullTitle = title ? `${title} | The Wise Compass` : defaultTitle;

  // Base path WITHOUT locale prefix (used to build hreflang en/fr/x-default).
  // If canonicalPath is provided we still strip /fr so the reciprocal links are correct.
  const rawBase = canonicalPath || pathname;
  const basePath = stripLocalePrefix(rawBase);
  // Canonical = exact current URL (with /fr for French, without for English)
  const currentPath = canonicalPath ? canonicalPath : pathname;
  const canonicalUrl = currentPath === "/" ? SITE_URL : `${SITE_URL}${currentPath.replace(/\/$/, "")}`;

  // Hreflang: always build EN (no prefix), FR (/fr prefix), and x-default (= EN) from basePath
  const enUrl = basePath === "/" ? SITE_URL : `${SITE_URL}${basePath.replace(/\/$/, "")}`;
  const frUrl = basePath === "/" ? `${SITE_URL}/fr` : `${SITE_URL}/fr${basePath.replace(/\/$/, "")}`;

  // Build the canonical 3-entry hreflang set. If a page passed custom alternates,
  // merge them by lang (override defaults) and always guarantee exactly one en, fr, x-default — no duplicates.
  const defaults: Record<string, string> = {
    en: enUrl,
    fr: frUrl,
    "x-default": enUrl,
  };
  if (hreflangAlternates) {
    for (const { lang, href } of hreflangAlternates) {
      if (lang === "en" || lang === "fr" || lang === "x-default") {
        defaults[lang] = href;
      }
    }
  }
  const alternates = [
    { lang: "en", href: defaults.en },
    { lang: "fr", href: defaults.fr },
    { lang: "x-default", href: defaults["x-default"] },
  ];

  const htmlLang = locale === "fr_FR" ? "fr" : "en";

  return (
    <Helmet>
      {/* Dynamic html lang attribute for crawlers */}
      <html lang={htmlLang} />

      {/* Canonical URL - matches current language version */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, follow" />}

      {/* Standard Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />

      {/* Open Graph (Facebook/LinkedIn) */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={locale} />
      <meta property="og:locale:alternate" content={alternateLocale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Hreflang for International SEO */}
      {alternates.map(({ lang, href }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={href} />
      ))}
    </Helmet>
  );
};

export default SEO;
